/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * TechSupportChat — floating Deskshare Support launcher and panel.
 *
 * Item 3 (Session 361): the widget is called "Deskshare Support". "Assistant"
 * described nothing to a customer who has just hit a problem, and it was never
 * applied consistently — five of the name-carrying keys were translated in each
 * language file and two were not, so a German visitor was shown two different
 * names on one page. Every visitor-facing string now lives in the six language
 * files; the message identifier below is NOT one of them.
 *
 * Architecture:
 *  - A launcher button in the bottom-right corner opens a sandboxed iframe
 *    panel that embeds the support chat application.
 *
 *    Item 4 (Session 361): the button sits in a zero-height sticky dock that
 *    ends where the footer begins, rather than being pinned to the window. A
 *    sticky element rides at the bottom of the window while there is content
 *    below it and comes to rest at the bottom edge of its container, so the
 *    button clears the site footer on a short page with no scroll listener, no
 *    measurement and no re-render. The panel keeps its fixed positioning: it
 *    is a dialog that deliberately floats over the page, and every comparable
 *    chat window on the web overlays a footer while it is open.
 *  - The panel is a dialog in the ARIA sense (role="dialog"). It is NOT a
 *    modal at desktop widths — see "Companion, not modal" below.
 *  - Cookie consent is respected: the launcher renders regardless of consent
 *    because the widget is a user-initiated first-party tool, not analytics.
 *  - The cookie banner (z-index ~9999) is above the launcher (z-index 9000)
 *    and the open panel (z-index 9100) so consent is never obscured.
 *  - Rendered on the product support page only, on Deskshare-operated hosts —
 *    see App.tsx.
 *
 * Companion, not modal (V-8 item 9):
 *  The panel used to be handed to the shared focus-trap hook with
 *  aria-modal="true". Together those did two things that are wrong beside a
 *  form the visitor is meant to keep filling in: assistive technology treated
 *  everything outside the panel as unavailable, so the support form behind it
 *  could not be read; and Tab was trapped inside the panel, so a keyboard user
 *  could not reach the form without dismissing the chat.
 *
 *  The shared hook also closes on any press outside the panel. On a modal that
 *  is right. Here it would mean every click on the support form — into the
 *  message box, onto the product dropdown, onto Submit — minimised the chat.
 *
 *  So this component now owns the three behaviours it actually needs (Escape
 *  minimises; opening moves focus in; closing returns focus to whichever
 *  control opened it) and the shared hook is left completely untouched for its
 *  other call sites: the cookie banner, the download dialog, the video player,
 *  the image zoom, the screenshot control, the date picker and the lost-key
 *  form. This is a smaller change than adding a parameter to the hook, not a
 *  larger one, and it cannot regress any of them.
 *
 *  aria-modal is kept for the one case where the panel really is modal: below
 *  480px the stylesheet gives it the full width of the screen, and a maximised
 *  panel fills the screen entirely. At that size it covers the form, so saying
 *  it is a companion would let a screen-reader user wander into a form they
 *  cannot see.
 *
 * Load detection (V-8 item 11):
 *  The frame has no onLoad handler on purpose. A frame the browser has REFUSED
 *  to display still fires load, on the blank error document it puts there — so
 *  a load handler would report success for the single most likely release-day
 *  failure. Readiness comes from a message the chat application posts once it
 *  has actually started; nothing else can tell a working embed from a blocked
 *  one, because a cross-origin frame is opaque either way.
 *
 * iframe sandbox:
 *  allow-scripts allow-same-origin allow-forms allow-popups
 *
 *  The allow-same-origin flag is safe here because the chat service is on
 *  techsupport.deskshare.com, which is a DIFFERENT origin from deskshare.com.
 *  allow-same-origin grants the frame access to its own origin's storage — not
 *  the parent's. The parent page (deskshare.com) remains fully sandboxed.
 *  WARNING: if the chat URL is ever changed to the same origin as the website,
 *  remove allow-same-origin immediately — the combination would remove all sandbox
 *  protection and allow the frame to escape the sandbox entirely.
 */

import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import {
  WindowCloseIcon,
  WindowMinimizeIcon,
  WindowMaximizeIcon,
  WindowRestoreIcon,
  BotIcon,
} from "@/components/common/icons";
import { useWindowWidth } from "@/hooks/useWindowWidth";
import {
  applyChatScope,
  buildTechSupportChatUrl,
  pendingFreshChat,
} from "@/utils/techSupportChat";
import {
  CHAT_RETURN_HISTORY_PARAM,
  CHAT_RETURN_HISTORY_VALUE,
  CHAT_RETURN_MAXIMIZED_VALUE,
  CHAT_RETURN_OPEN_PARAM,
  CHAT_RETURN_OPEN_VALUE,
  getSignInEntryUrlForChat,
  getTechSupportChatBaseUrl,
} from "@/utils/siteConfig";
import { readCookiePrefs } from "@/utils/cookiePrefs";
import {
  SESSION_CHANGED_EVENT,
  SESSION_KEY,
  getSessionToken,
} from "@/lib/auth-client";
import { getJsonWebToken } from "@/lib/http-client";
import { useSupportChat } from "@/context/supportChat";
import { PRODUCT_QUERY_PARAM_MAP } from "@/pages/support/TechnicalSupportComponents/constants";

/**
 * PH-5: the parameter the support chat's own sign-in return uses to name the
 * product it was opened for (the same name its frame address carries), and
 * the product names it may hold.
 */
const CHAT_RETURN_PRODUCT_NAME_PARAM = "deskshare-product-name";
const RETURN_PRODUCT_NAMES: ReadonlySet<string> = new Set(
  Object.values(PRODUCT_QUERY_PARAM_MAP),
);

/** The ?product= key the support form reads for a product's display name. */
function productKeyFor(displayName: string): string {
  if (!displayName) return "";
  const entry = Object.entries(PRODUCT_QUERY_PARAM_MAP).find(
    ([, name]) => name === displayName,
  );
  return entry ? entry[0] : "";
}
import "../../static/css/common/techSupportChat.css";

/**
 * Panel lifecycle:
 *   closed     — never opened, or dismissed with the X. Launcher visible, and
 *                the frame is NOT on the page.
 *   minimized  — put away temporarily. Launcher visible. The frame stays
 *                mounted and hidden, so the conversation survives; reopening
 *                restores the size the visitor last chose.
 *   closing    — P-1: the one task between pressing the X and the frame being
 *                taken off the page. Hidden by the same rule that hides the
 *                minimised panel, so the visitor sees it disappear at once,
 *                but the frame is still alive for long enough to receive the
 *                close message. Never a resting state.
 *   open       — default size.
 *   maximized  — near full-screen on desktop, full-screen on mobile.
 *
 * P-1 (Session 376). Minimise and Close used to do exactly the same thing to
 * the frame — both took it off the page — so the two controls were
 * indistinguishable to a visitor: whatever they saw on reopening came from the
 * chat's own per-tab storage, either way. On a shared computer that meant the
 * next person could read the previous person's conversation.
 *
 * Now they differ in the only way that matters. Minimise hides the panel and
 * keeps the frame, so the conversation is genuinely preserved rather than
 * accidentally restored. Close tells the chat the visitor has finished, marks
 * the next opening as a fresh session, and then unmounts the frame.
 */
type PanelState = "closed" | "minimized" | "closing" | "open" | "maximized";
type PanelSize = "open" | "maximized";

/** pending → the frame is starting. ok → the chat said it is ready. */
type LoadState = "pending" | "ok" | "failed";

const PANEL_ID = "tsc-panel";

/**
 * How long to wait for the chat's readiness message before showing the
 * failure panel. Long enough for a slow connection, short enough that a
 * visitor is not left looking at a blank rectangle.
 */
const LOAD_TIMEOUT_MS = 8000;

/**
 * The message the chat application posts to its framing page once it has
 * started: parent.postMessage({ type: CHAT_READY_MESSAGE }, "*").
 *
 * This is the first and only place the website sends or receives a message
 * between windows. It is the right tool for exactly one job — knowing whether
 * something loaded inside a frame actually started — and should not be reached
 * for as a general way to talk to the chat. Anything else the two need to say
 * to each other belongs in the address, where it can be read, logged and
 * reasoned about.
 */
export const CHAT_READY_MESSAGE = "deskshare-assistant:ready";

/**
 * F-1/F-3: the identity contract with the chat application.
 *
 * Four names, in two directions:
 *   ready         chat → website   the frame has started (above)
 *   token-request chat → website   send me an identity now
 *   token         website → chat   here is a short-lived token
 *   signed-out    website → chat   there is no visitor identity
 *
 * A MISMATCH IN ANY OF THESE FAILS SILENTLY. Nothing throws, nothing is
 * logged, and no error reaches monitoring on either side — the chat simply
 * opens, works perfectly, and never recognises anybody, which looks identical
 * to a visitor who is not signed in. No test on this side can detect it,
 * because the other half lives in another application. That is why the deploy
 * is gated on the chat team confirming these four names in writing, and why
 * they are not translated, renamed or "tidied" with the widget's visible name.
 */
export const CHAT_TOKEN_REQUEST_MESSAGE = "deskshare-assistant:token-request";
export const CHAT_TOKEN_MESSAGE = "deskshare-assistant:token";
export const CHAT_SIGNED_OUT_MESSAGE = "deskshare-assistant:signed-out";

/**
 * P-1: the fifth name in the same contract, and the same silent-failure shape.
 *
 *   close  website → chat  the visitor pressed the X; forget this conversation
 *
 * Posted on Close (X) ONLY — never on Minimize, which is now the control that
 * deliberately keeps the conversation. The chat application must clear its
 * stored conversation when it receives this. Until it does, the website
 * behaves exactly as it did before and nothing reports it, which is why the
 * chat team's written confirmation is a deploy gate. The `deskshare-fresh=1`
 * flag on the next opening is the second mechanism, for the case where this
 * message is missed or the page was reloaded in between.
 */
export const CHAT_CLOSE_MESSAGE = "deskshare-assistant:close";

/**
 * X-1: the sixth name in the same contract, and the same silent-failure shape.
 *
 *   login-request  chat → website  the visitor pressed Log in inside the chat
 *
 * The website answers by navigating THIS page to its own sign-in route with a
 * redirect back to where the visitor is now, so they return to the page
 * holding the chat rather than to a second tab.
 *
 * Until the chat ships its half nothing sends this and the branch never runs;
 * until this side ships, the chat's own anchor opens a new tab, which works.
 * So the two halves can be released in either order — which is NOT true of the
 * other five names and is worth stating. Raised by the Technical Support
 * Chatbot review; that package declares only the five above, so this side
 * ships the name first.
 *
 * No payload is read. The message is a request, and the website decides where
 * sign-in lives.
 */
export const CHAT_LOGIN_REQUEST_MESSAGE = "deskshare-assistant:login-request";

/* Item 3: deliberately NOT renamed with the widget. This value is a contract
   between two applications, not a label anybody reads. Changing it on this
   side alone would break the readiness handshake — and because a refused frame
   still reports as loaded, the symptom would be the failure panel appearing on
   a chat that is working perfectly. */

/**
 * 480, not 768. useWindowWidth only updates its value when the window crosses
 * the breakpoint it is given, so asking for 768 would leave the value stale
 * for every resize happening entirely below it — including 500px to 400px,
 * which is the only one that matters here.
 */
const FULL_SCREEN_BREAKPOINT = 480;

const TechSupportChat: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { selectedProduct, registerOpenChat } = useSupportChat();

  const [panelState, setPanelState] = useState<PanelState>("closed");
  // PH-1: read and cleared through the router, not around it.
  const [searchParams, setSearchParams] = useSearchParams();
  // The size the visitor last chose. Minimising preserves it so reopening
  // returns them to the same layout; closing resets it.
  const [lastSize, setLastSize] = useState<PanelSize>("open");
  const [bannerVisible, setBannerVisible] = useState(false);
  const [loadState, setLoadState] = useState<LoadState>("pending");
  const [iframeKey, setIframeKey] = useState(0);

  const panelRef = useRef<HTMLDivElement | null>(null);
  const launcherRef = useRef<HTMLButtonElement | null>(null);
  /**
   * F-1: the frame this component owns. Used to check that an incoming message
   * came from THIS frame and not merely from a page on the same origin.
   */
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  /** The control that opened the panel — the launcher, or the form's nudge. */
  const openerRef = useRef<HTMLElement | null>(null);
  /** One failure report per panel opening, not one per retry. */
  const reportedRef = useRef(false);

  /**
   * Two different questions, and P-1 turns on telling them apart.
   *
   *  isPanelVisible — can the visitor see it? Governs the three behaviours
   *    that are genuinely about what is on screen: Escape minimises, focus
   *    moves in on opening, focus returns to the launcher on leaving.
   *
   *  isPanelMounted — is the frame on the page? Governs every effect that
   *    exists to serve the frame: the failure timer, the readiness and
   *    identity listeners, and the sign-in/sign-out announcement.
   *
   * Getting this wrong is not cosmetic (N-2). The failure detector resets
   * whenever the panel leaves the screen and declares the chat broken eight
   * seconds later if it has not heard readiness. That was harmless while
   * minimising unmounted the frame, because reopening restarted the chat and
   * it announced itself again. With the frame kept alive it does not restart,
   * so a panel driven off `isPanelVisible` would report a false failure to
   * error monitoring and swap the frame for the "chat unavailable" screen —
   * destroying the very conversation this change exists to preserve, for every
   * visitor who minimises and reopens after eight seconds.
   */
  const isPanelVisible = panelState === "open" || panelState === "maximized";
  const isPanelMounted = panelState !== "closed";
  const isMaximized = panelState === "maximized";
  const isMinimized = panelState === "minimized";
  const isClosing = panelState === "closing";

  const windowWidth = useWindowWidth(FULL_SCREEN_BREAKPOINT);
  const isFullScreenPanel =
    windowWidth <= FULL_SCREEN_BREAKPOINT || isMaximized;

  // ── Frozen address ────────────────────────────────────────────────────────
  // V-8 item 10: the address is built once per opening, from the product and
  // language as they were at that moment, and not rebuilt while the panel is
  // on screen. Rebuilding it on every render — which is what calling the
  // builder in the component body did — means changing the dropdown behind the
  // open chat changes the src, which remounts the frame and destroys the
  // conversation. The nudge exists to get visitors to open the chat right
  // after choosing a product, so that is the main path, not a rare one.
  //
  // The theme is frozen with them, and that is wanted: a visitor whose
  // operating system switches to dark mode at sunset no longer has their
  // conversation reloaded underneath them.
  //
  // The memo depends on an object, which usually means it never memoises. Here
  // it does: the object is only ever replaced by handleOpen, so between one
  // open and the next it is the same object and the address is stable for the
  // whole life of the panel. Nothing resets it on close — harmless, and
  // deliberately not "fixed": a reset would add a render on every close and
  // nothing reads these values while the panel is shut.
  //
  // P-1: `fresh` joins them. It is frozen with the rest, for the same reason —
  // the address must not change under a live frame — and it is taken from
  // per-tab storage exactly once, at the moment the panel opens from closed.
  const [openedWith, setOpenedWith] = useState({
    product: "",
    lang: "",
    fresh: false,
    // PH-1 (Session 447): frozen with the rest, for the same reason - the
    // frame address must not change under a live frame.
    openHistory: false,
  });

  /*
   * Item 2, supporting change: the selected product is read from a reference
   * at the moment the panel opens.
   *
   * Not to avoid a stale value — the handler was already rebuilt whenever the
   * product changed, and that rebuild is the problem. It made the widget
   * re-register its open handler with the shared context on every change of
   * the dropdown. Holding the product in a reference makes handleOpen stable,
   * so the registration happens once. A layout effect keeps the reference
   * current before paint, so a click in the same frame as a change still reads
   * the new value.
   */
  const selectedProductRef = useRef(selectedProduct);
  useLayoutEffect(() => {
    selectedProductRef.current = selectedProduct;
  }, [selectedProduct]);

  /*
   * P-1: the panel state, read the same way and for the same reason.
   *
   * handleOpen has to know whether it is reopening a panel whose frame is
   * still alive or starting a new one, and it must stay stable — it is
   * registered with the support form through the shared context, and putting
   * panelState in its dependency list would re-register the handler on every
   * open, close and resize. A layout effect keeps this current before paint.
   */
  const panelStateRef = useRef(panelState);
  useLayoutEffect(() => {
    panelStateRef.current = panelState;
  }, [panelState]);

  /** P-1: the one-task delay in handleClose, cleared if the widget unmounts. */
  const closeTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
      }
    },
    [],
  );

  const chatUrl = useMemo(
    () =>
      buildTechSupportChatUrl(
        openedWith.product,
        openedWith.lang,
        openedWith.fresh,
        openedWith.openHistory,
      ),
    [openedWith],
  );

  /**
   * The same chat, full size, in a new tab. Built from the same frozen values
   * and without embed=1: a new tab wants the full-page layout, and linking to
   * the bare address would open the chat in whatever language the browser
   * prefers — undoing item 7 at the exact moment the visitor is already having
   * a bad experience.
   */
  const newTabUrl = useMemo(() => {
    const base = getTechSupportChatBaseUrl();
    const params = new URLSearchParams();
    // Item 2: the same rule as the frame's address. This link is built
    // separately and had the same gap — it said nothing when no product was
    // chosen, and a returning visitor was greeted about the product they last
    // asked about. It deliberately omits the embed flag, because a new tab
    // wants the full-size layout; the scope belongs on it for exactly the
    // reason it belongs on the frame.
    applyChatScope(params, openedWith.product);
    const lang = openedWith.lang.slice(0, 2).toLowerCase();
    if (lang) params.set("lang", lang);
    const query = params.toString();
    return query ? `${base}/?${query}` : `${base}/`;
  }, [openedWith]);

  // ── Readiness handshake ───────────────────────────────────────────────────
  // The chat application posts { type: "deskshare-assistant:ready" } to the
  // page that framed it once it has started. Waiting for the browser's load
  // event instead would be wrong: a frame the browser has refused to display
  // still fires load, on the blank document it puts there. A message can only
  // arrive if the chat actually ran.
  //
  // The exact origin comparison is the security-critical line. A listener that
  // does not check the sender accepts a message from any page holding a handle
  // on this window, including anything the chat itself opens in a new tab.
  //
  // Finding M (Session 361): the value compared against used to be the
  // configured chat address with a trailing slash removed. A message event
  // reports an origin only — scheme, host and port, never a path — so on any
  // environment configured with an address that carries a path, which is an
  // ordinary thing to do, the comparison could never match: the readiness
  // message was ignored and every visitor there was shown the failure message
  // after eight seconds on a chat that was working perfectly, with nothing
  // reporting why. Parsing the origin fixes that and keeps the check a plain
  // equality comparison, which is what makes it safe.
  //
  // N-4: the origin is resolved ONCE, defensively, and an unusable value is
  // treated as "no chat configured" rather than crashing the page. new URL()
  // throws on a relative or empty value, and this construction used to sit
  // inside the effect below — so a misconfigured address blanked the whole page
  // instead of failing just the chat panel. Every listener and every post below
  // is skipped when this is null.
  const chatOrigin = useMemo(() => {
    try {
      return new URL(getTechSupportChatBaseUrl()).origin;
    } catch {
      Sentry.captureMessage("chat_base_url_invalid", { level: "error" });
      return null;
    }
  }, []);

  // ── Open, close, minimise, maximise ───────────────────────────────────────

  /**
   * Close is a full dismissal: the conversation ends, and the next open starts
   * at the default size with a new conversation.
   *
   * The order here is the whole of correction 2 in P-1. Posting the message and
   * then setting state to "closed" in the same handler does not work: React
   * applies both changes together, the frame comes off the page in that same
   * update, and the browser discards the browsing context along with anything
   * still queued for it. The message is not merely unreliable — in the ordinary
   * case it is never delivered at all (N-1). So the panel is hidden first, by
   * CSS, and the frame is unmounted on the next task, which is not perceptible
   * to a visitor and is long enough for the message to land.
   *
   * The target origin is `chatOrigin`, never "*". The frame's address and this
   * origin are built from the same function and cannot disagree; a message
   * aimed at "*" would be handed to whatever origin occupied the frame if the
   * chat service ever redirected it. The identity token travels this same
   * channel, and a code base that has learned to write "*" for one payload will
   * eventually write it for the other.
   */
  const handleClose = useCallback(() => {
    const frame = iframeRef.current?.contentWindow;
    if (frame && chatOrigin) {
      frame.postMessage({ type: CHAT_CLOSE_MESSAGE }, chatOrigin);
    }

    // Survives a reload and a page change, unlike a ref (N-3).
    pendingFreshChat.set();
    setLastSize("open");
    setPanelState("closing"); // hidden by CSS, frame still alive

    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      // Only finish a close that is still in progress. The window is one task
      // and a visitor cannot realistically reopen inside it, but if anything
      // did — a scripted click, a test driving the component — this would
      // otherwise close the panel they had just reopened. Read from the ref
      // rather than from a state updater, because an updater has to be pure
      // and the two calls below are not.
      if (panelStateRef.current !== "closing") return;
      // The key change is what guarantees a brand-new browsing context on the
      // next opening, whatever the address turns out to be.
      setIframeKey((key) => key + 1);
      setPanelState("closed");
    }, 0);
  }, [chatOrigin]);

  // Minimise puts the panel away, remembers how big it was, and now genuinely
  // preserves the conversation: the frame stays mounted and the stylesheet
  // hides it. No message is sent, because nothing has ended.
  //
  // P-1: this does leave a live third-party frame on the product support page
  // for the rest of the visit rather than taking it down each time the panel is
  // put away. That is normal for chat widgets and it is what preserves the
  // conversation, but it was agreed rather than assumed — the question was
  // raised in the previous handoff, and this round answers it. The chat team
  // are asked to confirm in writing that the chat is content to keep running in
  // a hidden frame; that row is in the handoff.
  const handleMinimize = useCallback(() => {
    setPanelState((prev) => {
      if (prev === "open" || prev === "maximized") {
        setLastSize(prev);
      }
      return "minimized";
    });
  }, []);

  /**
   * Reopening returns to the remembered size.
   *
   * P-1 splits this in two. Coming back from "minimized" the frame is still
   * alive and the conversation is in it, so the address is left exactly as it
   * was — recomputing it would produce a different string (the fresh-session
   * flag alone would drop off), React would write a new `src`, and the browser
   * would reload the frame and destroy the conversation minimise exists to
   * keep. Opening from "closed" is the moment a new frame starts, so that is
   * where the address is frozen and where the fresh-session flag is taken from
   * storage — taken, not read: it must be honoured exactly once.
   */
  const handleOpen = useCallback(() => {
    if (panelStateRef.current !== "minimized") {
      setOpenedWith({
        product: selectedProductRef.current,
        lang: i18n.language,
        fresh: pendingFreshChat.take(),
        openHistory: false,
      });
    }
    setPanelState(lastSize);
  }, [lastSize, i18n.language]);

  /**
   * PH-1 (Session 447): come back into the chat after a sign-in started from
   * inside it.
   *
   * The markers are cleared through the ROUTER rather than with
   * window.history.replaceState. This is a React Router application and this
   * page reads its own query string; changing the address underneath the
   * router leaves the router holding the old location, so anything reading
   * search parameters afterwards sees parameters that are no longer in the
   * address bar. Replace rather than push, so Back does not return to an
   * address that would open the panel again.
   *
   * The pending-fresh-chat flag is deliberately NOT consumed here. It looks
   * like an omission and is not: a customer who pressed Close, signed in, and
   * is being returned to their past conversations must not have that flag
   * silently taken on the way, or the next chat they open on purpose would
   * continue an old conversation instead of starting the new one they asked
   * for.
   */
  const postLoginReturnHandledRef = useRef(false);
  useEffect(() => {
    if (postLoginReturnHandledRef.current) return;
    const marker = searchParams.get(CHAT_RETURN_OPEN_PARAM);
    if (
      marker !== CHAT_RETURN_OPEN_VALUE &&
      marker !== CHAT_RETURN_MAXIMIZED_VALUE
    ) {
      return;
    }

    postLoginReturnHandledRef.current = true;
    const openHistory =
      searchParams.get(CHAT_RETURN_HISTORY_PARAM) === CHAT_RETURN_HISTORY_VALUE;

    // PH-5 (Session 484): the product the chat was opened for, when the
    // sign-in return carries one. The support chat's own return path names
    // it as deskshare-product-name=<display name> (the chat-frame parameter);
    // ?product=<key> (DictationPro, the form the support form reads) is read
    // too, so either works. The support form's own selection wins when it
    // already has one.
    //
    // What must still give the general scope: neither parameter, an unknown
    // value, a display name that is not one of this site's products, and a
    // key the map only inherits (constructor, __proto__, toString) - hence
    // the own-property check rather than a plain lookup.
    const returnName = (
      searchParams.get(CHAT_RETURN_PRODUCT_NAME_PARAM) ?? ""
    ).trim();
    const returnKey = searchParams.get("product") ?? "";
    const returnProduct = RETURN_PRODUCT_NAMES.has(returnName)
      ? returnName
      : Object.prototype.hasOwnProperty.call(PRODUCT_QUERY_PARAM_MAP, returnKey)
        ? PRODUCT_QUERY_PARAM_MAP[returnKey]
        : "";

    const next = new URLSearchParams(searchParams);
    next.delete(CHAT_RETURN_OPEN_PARAM);
    next.delete(CHAT_RETURN_HISTORY_PARAM);
    // PH-5: the chat's product name belongs to this return only. The product
    // is handed to the support form as ?product=<key>, the one parameter the
    // form reads, so the form preselects it too; ?product= then stays, and is
    // harmless on a reload because the markers above are gone.
    next.delete(CHAT_RETURN_PRODUCT_NAME_PARAM);
    // When the chat named the product, its key replaces any other ?product=,
    // so the chat and the support form show the same product.
    const returnProductKey = productKeyFor(returnProduct);
    if (
      returnProductKey &&
      (returnProduct === returnName || !next.has("product"))
    ) {
      next.set("product", returnProductKey);
    }
    setSearchParams(next, { replace: true });

    setOpenedWith({
      product: selectedProductRef.current || returnProduct,
      lang: i18n.language,
      fresh: false,
      openHistory,
    });
    // SU-1: the size comes from the marker, not from lastSize. lastSize is
    // component state and starts as "open" on every page load, so the
    // maximized size chosen before the sign-in round trip was lost.
    const size: PanelSize =
      marker === CHAT_RETURN_MAXIMIZED_VALUE ? "maximized" : "open";
    setLastSize(size);
    setPanelState(size);
  }, [searchParams, setSearchParams, i18n.language]);

  /** Opened from the launcher: remember it, so focus returns there. */
  const handleLauncherClick = useCallback(() => {
    openerRef.current = launcherRef.current;
    handleOpen();
  }, [handleOpen]);

  /**
   * Opened from the form's nudge button, through the context. The element that
   * had focus when the click arrived is the nudge, so it is captured here
   * rather than asking the form to pass a ref across the context boundary.
   * Falls back to the launcher when there is no usable opener.
   */
  const handleOpenFromContext = useCallback(() => {
    const active = document.activeElement;
    openerRef.current =
      active instanceof HTMLElement && active !== document.body ? active : null;
    handleOpen();
  }, [handleOpen]);

  const handleToggleMaximize = useCallback(() => {
    setPanelState((prev) => {
      const next: PanelSize = prev === "maximized" ? "open" : "maximized";
      setLastSize(next);
      return next;
    });
  }, []);

  const handleRetry = useCallback(() => {
    setLoadState("pending");
    setIframeKey((key) => key + 1);
  }, []);

  // ── Register with the support form ────────────────────────────────────────
  // Unregistering on unmount is what makes the nudge disappear if this widget
  // is removed — including by its error boundary — rather than leaving a
  // button on the form that does nothing.
  useEffect(() => {
    registerOpenChat(handleOpenFromContext);
    return () => registerOpenChat(null);
  }, [registerOpenChat, handleOpenFromContext]);

  // ── Escape minimises ──────────────────────────────────────────────────────
  // T-7 is preserved: Escape minimises rather than closes, so the launcher
  // stays and the last size is remembered. Only the X fully closes. What is
  // deliberately NOT preserved is the outside-click half of that behaviour —
  // see "Companion, not modal" at the top of this file.
  useEffect(() => {
    if (!isPanelVisible) return;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleMinimize();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isPanelVisible, handleMinimize]);

  // ── Focus in on open, back to the opener on close ─────────────────────────
  // Focusing the panel container rather than its first button means a screen
  // reader announces the panel's own label, which is what tells the visitor
  // where they have arrived. tabIndex={-1} makes that possible without putting
  // the container into the Tab order.
  const wasVisible = useRef(false);
  useEffect(() => {
    if (isPanelVisible && !wasVisible.current) {
      panelRef.current?.focus();
    } else if (!isPanelVisible && wasVisible.current) {
      (openerRef.current ?? launcherRef.current)?.focus();
    }
    wasVisible.current = isPanelVisible;
  }, [isPanelVisible]);

  /**
   * F-1: hand the frame a short-lived identity, or tell it there is none.
   *
   * The token is delivered by message rather than placed in the frame's
   * address: that keeps it out of browser history, out of the Referer header
   * and out of any server log.
   *
   * The audience is the chat origin — the SAME string used to build the frame's
   * address, derived from the same function. The backend compares it exactly
   * against its allow-list, so the two must never be configured separately: if
   * they drift, every mint is refused and the chat silently never recognises
   * anybody.
   *
   * F-1 correction 5: a visitor with no session gets the signed-out message,
   * never a token message carrying null. Two ways of saying the same thing
   * means the chat has to handle both, and one of them is eventually handled
   * wrongly.
   */
  const sendIdentityToChat = useCallback(async (): Promise<void> => {
    const frame = iframeRef.current?.contentWindow;
    if (!frame || !chatOrigin) return;

    if (!getSessionToken()) {
      frame.postMessage({ type: CHAT_SIGNED_OUT_MESSAGE }, chatOrigin);
      return;
    }

    const token = await getJsonWebToken(chatOrigin);
    // The frame can be gone by the time the mint resolves.
    const stillThere = iframeRef.current?.contentWindow;
    if (!stillThere) return;

    if (!token) {
      // The mint was refused or failed. getJsonWebToken has already reported
      // it once, with the audience as a tag — nothing more is reported here
      // (F-2 correction 3). From the chat's point of view this visitor is
      // simply not identified.
      stillThere.postMessage({ type: CHAT_SIGNED_OUT_MESSAGE }, chatOrigin);
      return;
    }

    stillThere.postMessage({ type: CHAT_TOKEN_MESSAGE, token }, chatOrigin);
  }, [chatOrigin]);

  // P-1: mounted, not visible. This listener exists to serve the frame, and the
  // frame now outlives the panel being on screen. Left on visibility, a chat
  // that asked for a fresh token while minimised would be ignored.
  useEffect(() => {
    if (!isPanelMounted || !chatOrigin) return;
    const onMessage = (event: MessageEvent): void => {
      if (event.origin !== chatOrigin) return;
      // F-1 correction 1: check WHO sent it, not only where it came from.
      // Any window on that origin can post to this page — a pop-up the chat
      // itself opened, or a second frame — and would otherwise be handed a
      // token minted for this visitor.
      if (event.source !== iframeRef.current?.contentWindow) return;

      const type = (event.data as { type?: string })?.type;
      if (type === CHAT_READY_MESSAGE) {
        setLoadState("ok");
        // The chat has started, so it can receive the identity now.
        void sendIdentityToChat();
        return;
      }
      if (type === CHAT_TOKEN_REQUEST_MESSAGE) {
        // F-1 correction 2: there is NO timer re-sending the token shortly
        // after the panel opens. A timer is a guess about how long a frame
        // takes to start, and it is wrong in both directions — in the normal
        // case it fires into a frame that has not started, and in the slow case
        // it fires before the listener on the other side exists. The two real
        // events cover it: the chat announces readiness, and the chat can ask
        // at any time. Asking again is also how the chat refreshes an expired
        // token; the token lives fifteen minutes and only the chat can notice
        // that it has gone stale.
        void sendIdentityToChat();
        return;
      }
      if (type === CHAT_LOGIN_REQUEST_MESSAGE) {
        // X-1: the same route the header's Sign In control uses.
        // getSignInEntryUrl() already appends ?redirect= pointing at the page
        // the visitor is on, and safeRedirect.ts refuses a redirect back to a
        // sign-in page, so the value cannot loop. The chat is only rendered on
        // the product support page, so the visitor returns there — to the page
        // holding the conversation they were in the middle of.
        //
        // A full navigation rather than a router push: signing in replaces the
        // page, and the chat frame must not survive the transition holding a
        // token minted for the anonymous visitor who asked to sign in.
        //
        // This branch sits INSIDE the listener's existing event.origin and
        // event.source checks and inherits both, deliberately. A login-request
        // is a navigation instruction, and a page that navigates on any
        // message from that origin is a page anything on that origin can
        // steer. The sandbox is NOT widened to compensate: adding
        // allow-popups-to-escape-sandbox would loosen a sandbox that already
        // carries allow-same-origin and buy nothing, because nothing is
        // broken — removing the popup is the correct direction.
        //
        // PH-1 (Session 447): the chat-specific entry URL. It is
        // getSignInEntryUrl with two markers added to the return destination,
        // so the page the customer comes back to reopens the panel and asks
        // the chat for the past-conversations drawer. Without them the panel
        // comes back closed - nothing in this code reopens it - and the
        // conversation they were reading is two further steps away.
        // SU-1 (Session 474): and the size the panel had, so a customer who
        // maximized the chat gets it back maximized after signing in.
        window.location.assign(
          getSignInEntryUrlForChat(
            i18n.language,
            panelStateRef.current === "maximized",
          ),
        );
        return;
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [
    isPanelMounted,
    iframeKey,
    chatOrigin,
    sendIdentityToChat,
    i18n.language,
  ]);

  // ── F-3: tell the live frame when the visitor signs in or out ─────────────
  // The listener is attached only while the frame is on the page, so a sign-out
  // on any other page of the site costs nothing.
  //
  // P-1 corrected this one too, and the report did not call it out. A visitor
  // who minimises the chat and then signs out in another tab must still have
  // that announced to the chat, because the frame is still running and still
  // holds an identity. On visibility it would have been missed silently.
  useEffect(() => {
    if (!isPanelMounted || !chatOrigin) return;

    const onSessionChanged = (): void => {
      void sendIdentityToChat();
    };

    const onStorage = (event: StorageEvent): void => {
      // F-3 correction 1: only OUR key. The storage event fires for every key
      // any other tab writes — cookie preferences, the language preference,
      // the forum flag — and without this filter every one of them would
      // trigger a token mint.
      //
      // The null check is deliberate rather than an oversight: a storage clear
      // reports a null key, and that really does mean the session is gone.
      if (event.key !== null && event.key !== SESSION_KEY) return;
      onSessionChanged();
    };

    window.addEventListener(SESSION_CHANGED_EVENT, onSessionChanged);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(SESSION_CHANGED_EVENT, onSessionChanged);
      window.removeEventListener("storage", onStorage);
    };
  }, [isPanelMounted, chatOrigin, sendIdentityToChat]);

  // ── Failure timeout ───────────────────────────────────────────────────────
  // Catches both failures the handshake is designed for — a host that never
  // responds, and a service that refuses to be embedded — because neither
  // sends the readiness message.
  //
  // P-1 correction 1 (N-2), and the single most important line in this change.
  // This must follow the frame's life, not the panel's visibility. On
  // visibility, minimising resets the state to "pending" and reopening starts a
  // fresh eight-second timer — but the chat has not restarted, so it never
  // sends readiness a second time, the timer fires, a false failure is reported
  // to error monitoring, and the fallback screen replaces the frame. Replacing
  // the frame unmounts it, which destroys the conversation this whole change
  // exists to preserve. Every visitor who minimised and reopened after eight
  // seconds would have met it.
  useEffect(() => {
    if (!isPanelMounted) {
      setLoadState("pending");
      reportedRef.current = false;
      return;
    }
    const timer = window.setTimeout(() => {
      setLoadState((state) => {
        if (state !== "pending") return state;
        if (!reportedRef.current) {
          reportedRef.current = true;
          // Reported once per panel opening and not again on retry: a
          // genuinely unreachable service is one report per visitor rather
          // than one per button press, and a customer on a slow connection who
          // retries successfully does not generate a second report.
          Sentry.captureMessage(
            "Deskshare Support: chat frame did not load within the timeout",
            {
              level: "warning",
              tags: { component: "TechSupportChat" },
              extra: { chatUrl },
            },
          );
        }
        return "failed";
      });
    }, LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [isPanelMounted, chatUrl, iframeKey]);

  // ── Cookie banner offset ──────────────────────────────────────────────────
  useEffect(() => {
    // A-14: readCookiePrefs() reads ds_cookie_prefs, the structured record
    // written by CookieConsent.tsx. (This comment previously named
    // ds_cookie_consent, the companion flag, which the code stopped reading.)
    // A null return means no valid record, so the banner is or will be shown.
    const checkBanner = (): boolean => readCookiePrefs() === null;

    setBannerVisible(checkBanner());

    // Re-check when the user interacts with the consent banner.
    // Two events cover both cases:
    //  1. ds:cookie-consent-saved — dispatched by CookieConsent.tsx on the SAME
    //     tab when the user accepts or declines. The native `storage` event does
    //     NOT fire for same-tab writes, so without this custom event the offset
    //     would never clear after clicking Accept.
    //  2. storage — fires when localStorage changes from a DIFFERENT tab,
    //     keeping the offset in sync across tabs.
    const handleStorageChange = (): void => setBannerVisible(checkBanner());
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("ds:cookie-consent-saved", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        "ds:cookie-consent-saved",
        handleStorageChange,
      );
    };
  }, []);

  // P-1: the two hidden states are classes rather than an unmount. The
  // stylesheet gives both `display: none`, which also takes the panel out of
  // the accessibility tree — so no aria-hidden is set here. Adding one would
  // change nothing and would become a hazard the day something inside the
  // hidden panel holds focus.
  const panelClassName = [
    "tsc-panel",
    isMaximized ? "tsc-panel--maximized" : "",
    isMinimized ? "tsc-panel--minimized" : "",
    isClosing ? "tsc-panel--closing" : "",
    bannerVisible ? "tsc-panel--banner-offset" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // Item 4: the cookie-banner allowance moves to the dock, not the button.
  // The banner is a fixed overlay, so it is not something sticky positioning
  // can see; the footer is ordinary page content, so it is. Taking the larger
  // of the two allowances rather than adding them is the whole point — both
  // obstructions occupy the same strip at the bottom of the window, and the
  // banner covers the footer while it is showing.
  const dockClassName = [
    "tsc-dock",
    bannerVisible ? "tsc-dock--banner-offset" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      {/* ── Launcher button, in its sticky dock ───────────────────────────── */}
      <div className={dockClassName}>
        <button
          ref={launcherRef}
          type="button"
          className="tsc-launcher"
          aria-label={t("tech_support_chat.launcher_label")}
          aria-expanded={isPanelVisible}
          aria-haspopup="dialog"
          aria-controls={PANEL_ID}
          onClick={handleLauncherClick}
          /* Hidden only while the panel is on screen. In the minimised state the
           launcher stays available — that is what makes minimise different
           from close.

           Finding I: this attribute alone did nothing, because `.tsc-launcher`
           sets `display: flex` and any rule of ours outranks the browser's
           built-in `[hidden]` rule. The stylesheet now carries an explicit
           `.tsc-launcher[hidden] { display: none; }` — do not remove it. */
          hidden={isPanelVisible}
        >
          <BotIcon className="tsc-bot-icon" />
          <span className="tsc-launcher-text">
            {t("tech_support_chat.launcher_text")}
          </span>
        </button>
      </div>

      {/* ── Chat panel ─────────────────────────────────────────────────────
          P-1: rendered while MOUNTED. Minimised and closing are hidden by the
          stylesheet, so the frame — and with it the conversation — survives. */}
      {isPanelMounted && (
        <div
          id={PANEL_ID}
          ref={panelRef}
          tabIndex={-1}
          className={panelClassName}
          role="dialog"
          /* Modal only where the panel genuinely covers the form: below 480px
             the stylesheet gives it the whole screen, and a maximised panel
             fills it entirely. At every other width it is a companion to the
             support form and must not claim otherwise. */
          aria-modal={isFullScreenPanel ? true : undefined}
          aria-label={t("tech_support_chat.panel_label")}
        >
          <div className="tsc-panel-header">
            <span className="tsc-panel-title-wrap">
              <BotIcon className="tsc-bot-icon" />
              <span className="tsc-panel-title">
                {t("tech_support_chat.panel_title")}
              </span>
            </span>
            {/* S-2: Minimize, then Maximize/Restore, then Close — the order
                Windows itself uses for window controls. Ordered in the markup
                rather than with a CSS order property, because keyboard tab
                order and screen-reader reading order follow the markup, not the
                visual layout. */}
            <div className="tsc-panel-controls">
              <button
                type="button"
                className="tsc-control-btn"
                aria-label={t("tech_support_chat.minimize")}
                onClick={handleMinimize}
              >
                <WindowMinimizeIcon className="tsc-control-icon" />
              </button>
              <button
                type="button"
                className="tsc-control-btn"
                aria-label={
                  isMaximized
                    ? t("tech_support_chat.restore")
                    : t("tech_support_chat.maximize")
                }
                /* P-3 correction 2 (N-15): no aria-pressed. The label above
                   already changes with the state, so a screen reader was saying
                   "Restore, toggle button, pressed" — the same fact twice, in
                   words that disagree. aria-pressed describes a control that
                   stays down; this one turns into a different control, which is
                   what a window control does. Keep the changing label, drop the
                   attribute. */
                onClick={handleToggleMaximize}
              >
                {isMaximized ? (
                  <WindowRestoreIcon className="tsc-control-icon" />
                ) : (
                  <WindowMaximizeIcon className="tsc-control-icon" />
                )}
              </button>
              {/* N-14: its own key, not the shared `accessibility.close`.
                  Every dismiss control on the site uses that key and its
                  English text is the single word "Close". This X no longer
                  means "put this away" — that is the minus button — it means
                  "end this conversation", which is the right behaviour on a
                  shared computer but is not something a visitor can guess from
                  an X. The title attribute puts the same sentence in front of a
                  mouse user as a tooltip. */}
              <button
                type="button"
                className="tsc-control-btn"
                aria-label={t("tech_support_chat.close")}
                title={t("tech_support_chat.close")}
                onClick={handleClose}
              >
                <WindowCloseIcon className="tsc-control-icon" />
              </button>
            </div>
          </div>

          {loadState === "failed" ? (
            <div className="tsc-fallback" role="alert">
              <p>{t("tech_support_chat.unavailable")}</p>
              <button
                type="button"
                className="tsc-fallback-btn"
                onClick={handleRetry}
              >
                {/* errors.retry, not common.retry. There is no common.retry in
                    any of the six locale files, and a missing key renders as
                    the key itself — the button would have read "common.retry"
                    to every customer in every language. This is the key the
                    downloads table and the system information page already use
                    for this button. */}
                {t("errors.retry")}
              </button>
              <a
                className="tsc-fallback-link"
                href={newTabUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("tech_support_chat.open_in_new_tab")}
              </a>
            </div>
          ) : (
            /* Sandbox permissions, unchanged:
                allow-scripts     — chat widget JS must run
                allow-same-origin — grants the frame access to its OWN origin's
                                    storage, not the parent's. Safe only while
                                    the chat is cross-origin. Never combine with
                                    a same-origin chat URL.
                allow-forms       — chat input submission
                allow-popups      — open-in-new-tab links inside the chat

              allow="clipboard-write" is Permissions Policy, not sandbox. Without
              it the frame's clipboard access is denied regardless of what the
              sandbox permits. The site's own Permissions-Policy header must also
              name the chat origins — see nginx.conf.

              No onLoad handler and no loading="lazy": a refused frame fires
              load too, so readiness comes from the chat's own message (see the
              effect above), and lazy loading would delay the frame starting at
              all — and with it the message the timeout waits for. */
            <iframe
              key={iframeKey}
              ref={iframeRef}
              src={chatUrl}
              title={t("tech_support_chat.iframe_title")}
              className="tsc-iframe"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              allow="clipboard-write"
            />
          )}
        </div>
      )}
    </>
  );
};

TechSupportChat.displayName = "TechSupportChat";
export default TechSupportChat;
